import { useState } from 'react';
import { adminApi, ApiError } from '../lib/api';
import { formatPlainDate } from '../../shared/deadline';
import Eyebrow from './Eyebrow';
import Button from './Button';

interface Props {
  scriptId: string;
  currentApproveBy: string | null;
  /** Called after a successful save so the parent can refresh the loaded
   *  script without refetching. */
  onSaved: (next: string | null) => void;
}

/**
 * Set (or clear) the script's client-facing approval deadline. Stored as a
 * plain date so the review page and email copy can render it without
 * timezone shifts.
 */
export default function ApproveByPanel({ scriptId, currentApproveBy, onSaved }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(currentApproveBy ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startEdit() {
    setDraft(currentApproveBy ?? '');
    setError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError(null);
    setDraft(currentApproveBy ?? '');
  }

  async function handleSave() {
    const trimmed = draft.trim();
    const next: string | null = trimmed === '' ? null : trimmed;
    setSaving(true);
    setError(null);
    try {
      const res = await adminApi.setScriptApproveBy(scriptId, next);
      onSaved(res.approve_by);
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      data-print-hide
      className="bg-card border border-line rounded-md px-5 py-4"
    >
      <div className="flex items-center justify-between mb-3">
        <Eyebrow>// Approve by</Eyebrow>
        {!editing && (
          <button
            onClick={startEdit}
            className="
              font-mono text-[10px] uppercase tracking-[0.12em]
              text-muted hover:text-fg transition-colors
            "
          >
            {currentApproveBy ? 'Edit' : 'Set'}
          </button>
        )}
      </div>

      {editing ? (
        <div>
          <input
            type="date"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleSave();
              if (e.key === 'Escape') cancelEdit();
            }}
            disabled={saving}
            autoFocus
            className="
              w-full font-mono text-[13px]
              bg-page border border-line rounded px-3 py-2 text-fg
              focus:outline-none focus:border-accent transition-colors
              disabled:opacity-50
            "
          />
          {error && (
            <p className="mt-2 text-accent text-[12px]">{error}</p>
          )}
          <p className="mt-2 text-faint text-[11px] leading-relaxed">
            Optional. Leave blank to clear.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={cancelEdit}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : currentApproveBy ? (
        <div>
          <p className="font-mono text-[13px] text-fg bg-page/60 border border-line rounded px-3 py-2">
            {formatPlainDate(currentApproveBy)}
          </p>
          <p className="mt-2 text-faint text-[11px]">
            Shown on the client review page to keep the project schedule on
            track.
          </p>
        </div>
      ) : (
        <p className="text-muted text-[13px] leading-relaxed">
          No approval date set. The review page won't show a deadline line.
        </p>
      )}
    </section>
  );
}
